/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import { mapActions, layerSelectors, mapSelectors } from '@opengeoweb/store';
import type { OverLayer } from './OverlayersSelect';
import OverlayersSelect from './OverlayersSelect';

interface OverlayersSelectConnectProps {
  mapId?: string;
}

const OverlayersSelectConnect: React.FC<OverlayersSelectConnectProps> = ({
  mapId,
}) => {
  const dispatch = useDispatch();
  const availableOverlayers = useSelector((store: CoreAppStore) =>
    layerSelectors.getOverlayersForMapId(store, mapId!),
  );
  const currentOverlayerId = useSelector((store: CoreAppStore) =>
    mapSelectors.getSelectedOverlayerByMapId(store, mapId!),
  );
  const overlayersWithNone = !availableOverlayers.some(
    (layer) => layer.id === 'none',
  )
    ? [...availableOverlayers, { id: 'none', name: 'None' }]
    : availableOverlayers;

  const selectedOverlayer: OverLayer = overlayersWithNone.find(
    (layer: OverLayer) => layer.id === currentOverlayerId,
  ) || { id: 'none', name: 'None' };

  const changeOverlayer = React.useCallback(
    (mapId: string, selectedOverlayer: OverLayer) => {
      dispatch(
        mapActions.changeOverlayer({
          mapId: mapId || '',
          selectedOverlayer: selectedOverlayer.id,
          origin: 'map',
        }),
      );
    },
    [dispatch],
  );

  return (
    <OverlayersSelect
      availableOverlayers={overlayersWithNone}
      mapId={mapId || ''}
      onChangeOverlayer={changeOverlayer}
      selectedOverlayer={selectedOverlayer}
    />
  );
};

export default OverlayersSelectConnect;
